import { AudioLines, FileText, Film, SquareKanban } from "lucide-react"
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react"
import { PersonAvatar } from "@/components/people/person-avatar"
import { useTranslation } from "@/hooks/use-translation"
import { cn } from "@/lib/utils"
import { CATEGORY_ORDER, isMentionAutocompleteCategory, type MentionCategory, type MentionItem } from "./types"

function MentionMediaThumb({ mediaType }: { mediaType?: MentionItem["mediaType"] }) {
  if (mediaType === "audio") {
    return (
      <span className="pe-mention-menu__thumb pe-mention-menu__thumb--icon" aria-hidden>
        <AudioLines />
      </span>
    )
  }
  if (mediaType === "video") {
    return (
      <span className="pe-mention-menu__thumb pe-mention-menu__thumb--icon" aria-hidden>
        <Film />
      </span>
    )
  }
  if (mediaType === "file") {
    return (
      <span className="pe-mention-menu__thumb pe-mention-menu__thumb--icon" aria-hidden>
        <FileText />
      </span>
    )
  }
  return <span className="pe-mention-menu__thumb pe-mention-menu__thumb--empty" />
}

function MentionThumb({ item }: { item: MentionItem }) {
  if (item.category === "person") return <PersonAvatar person={item.person ?? { email: "" }} size={24} />
  if (item.category === "task") {
    return (
      <span className="pe-mention-menu__thumb pe-mention-menu__thumb--icon" aria-hidden>
        <SquareKanban />
      </span>
    )
  }
  if (!item.imageUrl) return <MentionMediaThumb mediaType={item.mediaType} />
  if (!item.frontViewChrome) return <img src={item.imageUrl} alt="" className="pe-mention-menu__thumb" />
  return (
    <span className="pe-mention-menu__thumb-wrap pe-mention-menu__thumb-wrap--prop-front">
      <img src={item.imageUrl} alt="" className="pe-mention-menu__thumb pe-mention-menu__thumb--prop-front" />
    </span>
  )
}

export type MentionListHandle = {
  onKeyDown: (event: KeyboardEvent) => boolean
}

export type MentionAsyncItems = {
  load: (query: string, signal: AbortSignal) => Promise<MentionItem[]>
  minQuery: number
  hint: string
  loading: string
}

type Props = {
  items: MentionItem[]
  query?: string
  command: (item: MentionItem) => void
  asyncItems?: MentionAsyncItems
  categoryOrder?: MentionCategory[]
}

type Group = { key: string; heading: string | null; items: MentionItem[] }

const ASYNC_DEBOUNCE_MS = 250

function mentionRowKey(item: MentionItem): string {
  return `${item.category}:${item.id}:${item.stateId ?? ""}:${item.attrKey ?? ""}`
}

export const MentionList = forwardRef<MentionListHandle, Props>((props, ref) => {
  const { items, query = "", command, asyncItems, categoryOrder = CATEGORY_ORDER } = props
  const { t } = useTranslation("shot")
  const [selected, setSelected] = useState(0)
  const [asyncResult, setAsyncResult] = useState<{ query: string; items: MentionItem[] } | null>(null)
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([])
  const shouldScrollRef = useRef(false)
  const keyboardNavRef = useRef(false)
  const loadAsync = asyncItems?.load
  const minQuery = asyncItems?.minQuery ?? 0
  const asyncActive = loadAsync != null && query.trim().length >= minQuery

  useEffect(() => {
    if (!loadAsync || query.trim().length < minQuery) {
      setAsyncResult(null)
      return
    }
    const controller = new AbortController()
    const timer = window.setTimeout(() => {
      loadAsync(query.trim(), controller.signal)
        .then((loaded) => {
          if (!controller.signal.aborted) setAsyncResult({ query, items: loaded })
        })
        .catch(() => {
          if (!controller.signal.aborted) setAsyncResult({ query, items: [] })
        })
    }, ASYNC_DEBOUNCE_MS)
    return () => {
      window.clearTimeout(timer)
      controller.abort()
    }
  }, [query, loadAsync, minQuery])

  const asyncShown = asyncActive && asyncResult?.query === query ? asyncResult.items : null

  // Привязанные к шоту → диалог → референсы / полные списки. Камеры в меню не показываем.
  const groups = useMemo<Group[]>(() => {
    const listed = items.filter((item) => item.autocomplete !== false)
    const result: Group[] = []
    const pushCategory = (category: MentionCategory) => {
      if (!isMentionAutocompleteCategory(category)) return
      // Привязанные к шоту герои/локации уже показаны в верхнем блоке — не дублируем их ниже.
      const groupItems = listed.filter((it) => it.category === category && !it.pinned)
      if (groupItems.length > 0) {
        result.push({ key: category, heading: t(`promptEditor.category.${category}`), items: groupItems })
      }
    }

    const pinned = listed.filter(
      (it) => (it.category === "hero" || it.category === "loc" || it.category === "prop") && it.pinned,
    )
    if (pinned.length > 0) {
      result.push({ key: "pinned", heading: t("promptEditor.pinnedGroup"), items: pinned })
    }
    const attrs = listed.filter((it) => it.category === "attr")
    if (attrs.length > 0) {
      result.push({ key: "attr", heading: t("promptEditor.category.attr"), items: attrs })
    }
    pushCategory("reference")
    for (const category of categoryOrder) {
      if (category === "reference" || category === "attr") continue
      if (!isMentionAutocompleteCategory(category)) continue
      pushCategory(category)
    }
    // Строка, которая уже есть в синхронном списке, не повторяется из поиска.
    const known = new Set(listed.map(mentionRowKey))
    const asyncItemsShown = (asyncShown ?? []).filter(
      (item) => item.autocomplete !== false && !known.has(mentionRowKey(item)),
    )
    for (const category of categoryOrder) {
      const groupItems = asyncItemsShown.filter((it) => it.category === category && it.category !== "task")
      if (groupItems.length > 0) {
        result.push({
          key: `async-${category}`,
          heading: t(`promptEditor.category.${category}`),
          items: groupItems,
        })
      }
    }
    const tasks = asyncItemsShown.filter((it) => it.category === "task")
    if (tasks.length > 0) result.push({ key: "task", heading: null, items: tasks })
    return result
  }, [items, asyncShown, categoryOrder, t])

  const navEntries = useMemo<MentionItem[]>(() => groups.flatMap((group) => group.items), [groups])

  useEffect(() => setSelected(0), [items, query])

  useEffect(() => {
    itemRefs.current.length = navEntries.length
  }, [navEntries.length])

  useEffect(() => {
    if (!shouldScrollRef.current) return
    shouldScrollRef.current = false
    itemRefs.current[selected]?.scrollIntoView({ block: "nearest" })
  }, [selected])

  const pick = (navIndex: number) => {
    const item = navEntries[navIndex]
    if (!item) return
    command({
      category: item.category,
      id: item.id,
      label: item.label,
      stateId: item.stateId,
      attrKey: item.attrKey,
    })
  }

  const selectViaMouse = (navIndex: number) => {
    if (keyboardNavRef.current) return
    shouldScrollRef.current = false
    setSelected(navIndex)
  }

  useImperativeHandle(
    ref,
    () => ({
      onKeyDown: (event) => {
        if (navEntries.length === 0) return false
        if (event.key === "ArrowDown") {
          keyboardNavRef.current = true
          shouldScrollRef.current = true
          setSelected((s) => (s + 1) % navEntries.length)
          return true
        }
        if (event.key === "ArrowUp") {
          keyboardNavRef.current = true
          shouldScrollRef.current = true
          setSelected((s) => (s + navEntries.length - 1) % navEntries.length)
          return true
        }
        if (event.key === "Enter" || event.key === "Tab") {
          pick(selected)
          return true
        }
        return false
      },
    }),
    // pick closes over navEntries/command; selected needed for Enter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selected, navEntries, command],
  )

  if (navEntries.length === 0) {
    const empty = !asyncItems
      ? t("promptEditor.nothingFound")
      : !asyncActive
        ? asyncItems.hint
        : asyncShown == null
          ? asyncItems.loading
          : t("promptEditor.nothingFound")
    return (
      <div className="pe-mention-menu">
        <div className="px-3 py-2 text-[11px] text-ui-text-muted">{empty}</div>
      </div>
    )
  }

  let runningNavIndex = 0

  return (
    <div
      className="pe-mention-menu"
      onMouseMove={() => {
        keyboardNavRef.current = false
      }}
    >
      {groups.map((group, index) => (
        <div key={group.key} className="pe-mention-menu__group">
          {group.heading != null ? (
            <div className="pe-mention-menu__heading">{group.heading}</div>
          ) : index > 0 ? (
            <div role="separator" className="pe-mention-menu__separator" />
          ) : null}
          {group.items.map((item) => {
            const navIndex = runningNavIndex++
            const active = navIndex === selected
            return (
              <button
                key={`${group.key}:${item.category}:${item.id}:${item.stateId ?? ""}:${item.attrKey ?? ""}`}
                ref={(node) => {
                  itemRefs.current[navIndex] = node
                }}
                type="button"
                className={cn("pe-mention-menu__item", active && "is-active")}
                onMouseEnter={() => selectViaMouse(navIndex)}
                onMouseDown={(e) => {
                  e.preventDefault()
                  pick(navIndex)
                }}
              >
                {item.category !== "attr" ? <MentionThumb item={item} /> : null}
                <span className="pe-mention-menu__text">
                  <span className="pe-mention-menu__label">{item.label}</span>
                  {item.subtitle ? <span className="pe-mention-menu__subtitle">{item.subtitle}</span> : null}
                </span>
              </button>
            )
          })}
        </div>
      ))}
    </div>
  )
})

MentionList.displayName = "MentionList"
